import React, { forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react'
import { makeStyles } from '@mui/styles'
import { useDispatch, useSelector } from 'react-redux'
import { Form, Formik } from 'formik'
import { TextInput, ZipCodeInput } from '@/modules/base/components/inputs'
import { fetchCompanyData, submitCompanyData } from '@/redux/ducks/settings/company'
import lang from '@/lang'
import useCheckAccessRights from '@/modules/_helpers/accessCheck/useCheckAccessRights'
import { USER_RIGHTS } from '@/modules/base/util/constants'
import TenantContactPersonService, { TenantContactPersonData } from '@/modules/tenantContactPerson/tenantContactPersonService'
import * as Yup from 'yup'
import { UserService } from '@/modules/user/userService'

const useStyles = makeStyles(() => ({
  row: {
    display: 'flex',
    padding: '5px 0px',
    '& > *:first-child': {
      paddingRight: 5,
    },
    '& > *:last-child': {
      paddingLeft: 5,
    },
    '& > *:first-child:last-child': {
      padding: 0,
    },
  },
  zipCodeInput: {
    width: '50%',
  },
  cityInput: {
    width: '50%',
  },
}))

const validationSchema = Yup.object().shape({
  companyName: Yup.string(),
  street: Yup.string(),
  zipCode: Yup.string(),
  city: Yup.string(),
  firstName: Yup.string(),
  lastName: Yup.string(),
  phone: Yup.string(),
  website: Yup.string(),
  email: Yup.string()
    .email(' '),
})

const CompanyData = forwardRef(({ onSubmitted, onValid }, ref) => {
  const classes = useStyles()
  const dispatch = useDispatch()

  const readOnly = !useCheckAccessRights(USER_RIGHTS.SETTINGS.WRITE)
  const formikRef = useRef(null)
  useImperativeHandle(ref, () => ({
    save: async() => {
      await formikRef.current?.handleSubmit()
    },
  }))

  const companyData = useSelector((state: any) => state.settings.company)
  const userInfo = useSelector((state: any) => state.user.info)
  const [intialContactPerson, setIntialContactPerson] = useState<TenantContactPersonData>(null)

  useEffect(() => {
    dispatch(fetchCompanyData())

    TenantContactPersonService.getDefault().then(contactPerson => setIntialContactPerson(contactPerson))

  }, [dispatch])

  const initialValues = {
    companyName: companyData?.companyName || '',
    street: companyData?.street || '',
    zipCode: companyData?.zipCode || '',
    city: companyData?.city || '',
    firstName: intialContactPerson?.firstName || userInfo.firstName || '',
    lastName: intialContactPerson?.lastName || userInfo.lastName || '',
    phone: intialContactPerson?.phone || companyData?.phone1,
    email: intialContactPerson?.email || companyData?.email || userInfo.email,
    website: companyData?.website || '',
  }

  const onSubmitHandle = async(values) => {
    const contactPerson = {
      firstName: values['firstName'],
      lastName: values['lastName'],
      phone: values['phone'],
      email: values['email'],
    }
    if (values['firstName'] && values['lastName'] && values['phone'] && values['email']) {
      if (intialContactPerson?.id) {
        await TenantContactPersonService.update(intialContactPerson.id, contactPerson)
      } else {
        await TenantContactPersonService.createDefault(contactPerson)
      }
    }
    await UserService.editSelf(contactPerson)

    const newCompanyData = {
      ...companyData,
      companyName: values['companyName'],
      street: values['street'],
      zipCode: values['zipCode'],
      city: values['city'],
      website: values['website'],
    }
    if (!newCompanyData.phone1) {
      newCompanyData.phone1 = values['phone']
    }
    if (!newCompanyData.email) {
      newCompanyData.email = values['email']
    }
    dispatch(submitCompanyData(newCompanyData))
    onSubmitted()
  }

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={validationSchema}
      enableReinitialize={true}
      innerRef={formikRef}
      onSubmit={async(values) => {
        await onSubmitHandle(values)
      }}
      validateOnMount
    >
      {({ values, isValid, isSubmitting }) => {
        onValid(isValid && !isSubmitting)
        return (
          <Form>
            <div>
              <h6>{lang.onboardingTesterData.companyDataTitle}</h6>
              <div className={classes.row}>
                <TextInput
                  label={lang.companyNameLabel}
                  name="companyName"
                  useFormik
                  readOnly={readOnly}
                  placeholder={lang.missingCompanyData.placeholder}
                  labelTooltip={lang.onboardingTesterData.companyNameTooltip}
                />
              </div>
              <div className={classes.row}>
                <TextInput
                  name="street"
                  label={lang.streetFieldLabel}
                  readOnly={readOnly}
                  useFormik
                  placeholder={lang.missingCompanyData.placeholder}
                />
              </div>
              <div className={classes.row}>
                <ZipCodeInput
                  readOnly={readOnly}
                  zipCode={values?.zipCode}
                  city={values?.city}
                  useFormik
                  customPlaceholder={lang.missingCompanyData.placeholder}
                  customStyles={{ zipCodeInput: classes.zipCodeInput, cityInput: classes.cityInput }}
                />
              </div>
              <h6>{lang.onboardingTesterData.companyDataContact}</h6>
              <div className={classes.row}>
                <TextInput
                  label={lang.firstNameLabel}
                  name="firstName"
                  useFormik
                  readOnly={readOnly}
                  placeholder={lang.missingCompanyData.placeholder}
                />
                <TextInput
                  label={lang.lastNameLabel}
                  name="lastName"
                  useFormik
                  readOnly={readOnly}
                  placeholder={lang.missingCompanyData.placeholder}
                />
              </div>
              <div className={classes.row}>
                <TextInput
                  label={lang.onboardingTesterData.companyDataPhone}
                  name="phone"
                  useFormik
                  readOnly={readOnly}
                  placeholder={lang.missingCompanyData.placeholder}
                  labelTooltip={lang.onboardingTesterData.phoneNumberTooltip}
                />
                <TextInput
                  label={lang.onboardingV2.taxConsultantUser.inputs.email}
                  name="email"
                  useFormik
                  readOnly={readOnly}
                  placeholder={lang.missingCompanyData.placeholder}
                />
              </div>
            </div>
          </Form>
        )
      }
      }
    </Formik>
  )
})

export default CompanyData
